import { Content, Playlist, Presentation } from '@raydiant/api-client-js';
import immutable from 'object-path-immutable';
import { LocalFileUploads, PresentationUpload } from '../types';
import { isFileUploading, hasFileUploadError } from './fileUploadUtils';

export const getPresentationFileStatus = (
  presentation: Presentation,
  localUploads: LocalFileUploads,
) => {
  if (!presentation.fileUploads) {
    return { isUploading: false, hasError: false };
  }

  return {
    isUploading: presentation.fileUploads.some((fileUpload) =>
      isFileUploading(fileUpload, localUploads),
    ),
    hasError: presentation.fileUploads.some((fileUpload) =>
      hasFileUploadError(fileUpload, localUploads),
    ),
  };
};

/** Collect all presentation IDs from a playlist recursively (including nested playlists). Deduplicated. */
export const collectPresentationIds = (
  playlist: Playlist,
  getPlaylist: (playlistId: string) => Playlist | undefined,
  visitedPlaylistIds: Set<string> = new Set(),
): string[] => {
  const ids = new Set<string>();
  if (visitedPlaylistIds.has(playlist.id)) return [...ids];
  visitedPlaylistIds.add(playlist.id);
  const items = playlist.items ?? [];
  for (const item of items) {
    if (item.presentationId) ids.add(item.presentationId);
    if (item.playlistId) {
      const nested = getPlaylist(item.playlistId);
      if (nested)
        collectPresentationIds(nested, getPlaylist, visitedPlaylistIds).forEach(
          (id) => ids.add(id),
        );
    }
  }
  return [...ids];
};

/** Library Content (presentation) upload/error status for badge, overlay, and playlist-add restriction */
export const getContentFileStatus = (
  content: Content,
  localUploads: LocalFileUploads,
) => {
  if (content.resourceType !== 'presentation') {
    return { isUploading: false, hasError: false };
  }
  const fileUploads = content.presentationData?.fileUploads ?? null;
  if (!fileUploads?.length) {
    return { isUploading: false, hasError: false };
  }
  return {
    isUploading: fileUploads.some((fu) => isFileUploading(fu, localUploads)),
    hasError: fileUploads.some((fu) => hasFileUploadError(fu, localUploads)),
  };
};

// Inject local file upload URLs into the presentation. Used by the presentation preview
// to display the local file before it is uploaded.
export const injectPresentationUploads = (
  presentation: Presentation,
  uploads: PresentationUpload[],
): Presentation => {
  let presentationWithLocalFiles = presentation;

  for (const upload of uploads) {
    presentationWithLocalFiles = immutable.set(
      presentationWithLocalFiles,
      [...upload.path, 'url'] as string[],
      upload.localUrl,
    );
  }

  return presentationWithLocalFiles;
};
